import { GridApi, GridRowId } from '@mui/x-data-grid-pro';
import { MutableRefObject, useCallback, useState } from 'react';
import { ZodArray, ZodType, ZodSafeParseResult } from 'zod';

import { intakeDataGridCustomClasses } from './IntakeDataGrid.util';

interface UseDataGridStateProps<T extends Record<string, any>> {
  apiRef: MutableRefObject<GridApi>;
  schema: ZodArray<ZodType<T>>;
}

export const useGridValidator = <T extends Record<string, any>>({ apiRef, schema }: UseDataGridStateProps<T>) => {
  const getRawRow = (rowId: GridRowId) => {
    if (rowId) {
      return apiRef?.current.getRowModels?.().get(rowId) as T;
    }
  };
  const getRawRows = () => {
    return Array.from(apiRef?.current?.getRowModels?.().values() || []) as T[];
  };

  return {
    getRawRows,
    getRawRow,
    isValidRow: useCallback<(rowId: GridRowId) => ZodSafeParseResult<T>>(
      (rowId) => {
        return schema.element.safeParse(getRawRow(rowId));
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [schema],
    ),
    isValid: useCallback<() => ZodSafeParseResult<T[]>>(() => {
      return schema.safeParse(getRawRows());
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schema]),
    parseRow: useCallback<(rowId: GridRowId) => T>(
      (rowId) => {
        return schema.element.parse(getRawRow(rowId));
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [schema],
    ),
    parse: useCallback<() => T[]>(() => {
      return schema.parse(getRawRows());
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schema]),
  };
};

export const useGridValidationStyling = <T extends Record<string, any>>(
  apiRef: MutableRefObject<GridApi>,
  gridSchema: ZodArray<ZodType<T>>,
) => {
  const { isValidRow, isValid } = useGridValidator({
    apiRef,
    schema: gridSchema,
  });

  const getGridClassName = () => {
    const validation = isValid();

    const hasGridLevelIssue = validation.error?.issues.some((issue) => issue.path.length === 0);

    // we only want to highlight the grid, if there is a grid level issue (otherwise this conflicts with row level highlighting)
    return validation.success || !hasGridLevelIssue ? '' : intakeDataGridCustomClasses.error;
  };

  const getRowClassName = (lineItem: any) => {
    return isValidRow(lineItem.id).success ? '' : intakeDataGridCustomClasses.rowError;
  };

  return {
    getGridClassName,
    getRowClassName,
  };
};

type GridValidationMap = Record<GridRowId, boolean>;
export const useValidationTrigger = () => {
  const [validations, setValidations] = useState<GridValidationMap>({});

  const shouldValidate = (id: GridRowId) =>
    setValidations((prev) => ({
      ...prev,
      [id]: true,
    }));

  return {
    triggeredValidations: validations,
    shouldValidate,
  };
};
